import type { AmountString, Nav, VaultIdentifier, VaultTransaction, WEIString, } from "@bracket/types";
import type { IBracketStoreActions, IBracketStoreProps, } from "@store/types";
import type { VaultExposure, } from "@types";
import type { LoadedVaultWithPnl, } from "./resolvers/loaded/types";
import type { ManagedVault, ManagedVaultData, } from "./resolvers/mgmt/types";
import type { MorphoMarketDataSet, } from "./resolvers/morpho/types";

export type { TransactionWithPnl, } from "./resolvers/loaded/types";

export declare type TransactedStatus = "idle" | "pending" | "success" | "error";

export type { Vault, } from "@bracket/types";

export declare type VaultState = "IDENTIFIABLE" | "SHORT_LOADED" | "LOADED" | "MANAGEABLE";

/** known tranwsaction literal mode...  */
export declare type VaultTrxnMode = "WITHDRAW" | "DEPOSIT" | "WRAP" | "UNWRAP";


export declare type IVaultsProps = IBracketStoreProps & {
	acceptedKYCTerms: boolean;
	acceptedTerms: boolean;
	active: LoadedVaultWithPnl | null;
	activeAdminVault: LoadedVaultWithPnl & { mgmt: ManagedVaultData; } | null;
	activeSection: VaultSection;
	allowance: bigint;
	amount: AmountString;
	amountError: string | null;
	amountUSD: number;
	available: LoadedVaultWithPnl[];
	balance: AmountString;
	exposure: VaultExposure;
	fetchingItems: string[];
	hasInsufficientBalance: boolean;
	kyb_enabled: boolean;
	kyc_min_tier: number;
	kyc_required: boolean;
	morpho: MorphoMarketDataSet;
	needsApproval: boolean;
	participant: boolean;
	providing: ManagedVault[];
	transactedStatus: TransactedStatus;
	transacting: boolean;
	txnMode: VaultTrxnMode;
	vaultNotFound: boolean;

	_displayKyc: boolean;
	_displayWrappables: boolean;
}

export declare type IVaultsActions = IBracketStoreActions & {
	acceptKYCTerms: ( position: boolean, ) => void;
	acceptTerms: ( position: boolean, ) => void;
	approve: () => void;
	claim: ( transaction: VaultTransaction, ) => void;
	claimBrktTvlFees: () => void;
	claimManagementFees: () => void;
	claimManagementPerformanceFees: () => void;
	claimManagementTvlFees: () => void;
	deposit: () => void;
	fetch: ({ vault, }: { vault?: string; }) => Promise<void>;
	fetchHoldings: () => Promise<void>;
	onAmountChange: ( amount: AmountString, ) => void;
	setInitialAmounts: () => void;
	setMax: () => void;
	navigateTo: ( path: string, ) => void;
	updateVanityNav: ( nav: WEIString, ) => void;
	refetchActiveVault: () => Promise<void>;
	refetchActiveAdminVault: () => Promise<void>;
	setActiveSection: (section: VaultSection) => void;
	setActiveVault: ( identifier: VaultIdentifier, section?: VaultSection, navigateToVault?: boolean) => Promise<void>;
	setTxnMode: (mode: VaultTrxnMode) => void;
	updateNav: ( nav: Partial<Nav>, ) => void;
	withdraw: () => void;
}

export declare type IVaultsSlice = IBracketStoreProps & IVaultsProps & IVaultsActions;

/** a Vault with all data and extended managment/privileged data as well - everything we know about this vault */
// export declare type ManagedVault = LoadedVault & {
// 	mgmt: ManagedVaultData;
// };

export declare type VaultSection =
	| "terms-and-fees"
	| "description"
	| "transactions"
	| "faqs"
	| "nav-history"
	| "holdings"
	| "charts";

export declare type VaultSectionItem = {
	id: VaultSection;
	label: string;
	route?: `/${string}`;
	dev?: boolean;
	disabled?: boolean;
};

const VAULT_SECTIONS: VaultSection[] = [
	"terms-and-fees",
	"description",
	"transactions",
	"faqs",
	"nav-history",
	"holdings",
	"charts",
];

export const isVaultSection = (section: unknown): section is VaultSection => {
	return typeof section === "string" && VAULT_SECTIONS.includes(section as VaultSection);
};

export * from "./resolvers/loaded/types";
